import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal, Steps, Upload, Button, Alert, Table, Select, Tag, Tabs, Checkbox,
  Result, Row, Col, Statistic, Typography, Space, message
} from 'antd';
import {
  InboxOutlined, DownloadOutlined, FileExcelOutlined, CheckCircleOutlined,
  ExclamationCircleOutlined, CloseCircleOutlined
} from '@ant-design/icons';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';

const { Step } = Steps;
const { Dragger } = Upload;
const { TabPane } = Tabs;
const { Text, Paragraph } = Typography;
const MAX_ROWS = 2000;

const FIELD_OPTIONS = [
  { value: 'full_name', label: 'Họ tên', required: true, aliases: ['ho ten', 'ten khach hang', 'full name', 'customer name', 'name'] },
  { value: 'phone', label: 'Số điện thoại', required: true, aliases: ['so dien thoai', 'sdt', 'dien thoai', 'phone', 'phone number', 'mobile'] },
  { value: 'email', label: 'Email', aliases: ['email', 'email address', 'mail'] },
  { value: 'source', label: 'Nguồn khách hàng', aliases: ['nguon', 'nguon khach', 'kenh', 'kenh thu thap', 'source', 'channel'] },
  { value: 'address', label: 'Địa chỉ', aliases: ['dia chi', 'address'] },
  { value: 'note', label: 'Ghi chú', aliases: ['ghi chu', 'note', 'notes', 'description'] },
  { value: 'tags', label: 'Tags', aliases: ['tag', 'tags', 'nhan', 'nhom khach'] },
  { value: 'assigned_sale', label: 'Sale phụ trách (ID)', aliases: ['sale id', 'assigned sale', 'nhan vien id'] }
];

export const normalizeHeader = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/đ/g, 'd')
  .replace(/Đ/g, 'd')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, ' ')
  .trim();

export const guessMapping = (headers) => {
  const used = new Set();
  return headers.map((header) => {
    const normalized = normalizeHeader(header);
    const matched = FIELD_OPTIONS.find((field) => {
      if (used.has(field.value)) return false;
      return field.aliases.some((alias) => normalized === alias || normalized.includes(alias) || alias.includes(normalized));
    });
    if (matched) used.add(matched.value);
    return matched ? matched.value : undefined;
  });
};

const readExcelFile = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = (event) => {
    try {
      const workbook = XLSX.read(event.target.result, { type: 'array' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const matrix = XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
        defval: '',
        raw: false,
        blankrows: false
      });
      resolve(matrix);
    } catch (error) {
      reject(error);
    }
  };
  reader.onerror = reject;
  reader.readAsArrayBuffer(file);
});

const CustomerImportModal = ({ visible, onCancel, onSuccess, fetchData }) => {
  const [current, setCurrent] = useState(0);
  const [file, setFile] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [dataRows, setDataRows] = useState([]);
  const [mapping, setMapping] = useState([]);
  const [mappedCustomers, setMappedCustomers] = useState([]);
  const [preview, setPreview] = useState(null);
  const [activeTab, setActiveTab] = useState('valid');
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => {
    setCurrent(0);
    setFile(null);
    setHeaders([]);
    setDataRows([]);
    setMapping([]);
    setMappedCustomers([]);
    setPreview(null);
    setActiveTab('valid');
    setSkipDuplicates(true);
    setResult(null);
  };

  useEffect(() => {
    if (!visible) reset();
  }, [visible]);

  const handleFile = async (nextFile) => {
    const extension = (nextFile.name.split('.').pop() || '').toLowerCase();
    if (!['xlsx', 'xls'].includes(extension)) {
      message.error('Vui lòng chọn file .xlsx hoặc .xls');
      return false;
    }
    if (nextFile.size > 10 * 1024 * 1024) {
      message.error('File không được lớn hơn 10MB');
      return false;
    }

    try {
      const matrix = await readExcelFile(nextFile);
      if (!matrix.length || !matrix[0].length) throw new Error('File không có dữ liệu');
      if (matrix.length - 1 > MAX_ROWS) throw new Error(`Mỗi lần chỉ được import tối đa ${MAX_ROWS} khách hàng`);

      const nextHeaders = matrix[0].map((header, index) => String(header || `Cột ${index + 1}`).trim());
      const nextRows = matrix.slice(1).filter((row) => row.some((cell) => String(cell || '').trim()));
      if (!nextRows.length) throw new Error('File chỉ có header, chưa có dữ liệu khách hàng');

      setFile(nextFile);
      setHeaders(nextHeaders);
      setDataRows(nextRows);
      setMapping(guessMapping(nextHeaders));
      setPreview(null);
      message.success(`Đã đọc ${nextRows.length} dòng từ file`);
    } catch (error) {
      message.error(error.message || 'Không thể đọc file Excel');
    }
    return false;
  };

  const downloadTemplate = () => {
    const worksheet = XLSX.utils.aoa_to_sheet([
      ['Họ tên', 'Số điện thoại', 'Email', 'Nguồn', 'Địa chỉ', 'Tags', 'Ghi chú'],
      ['Nguyễn Văn A', '0901234567', 'a@example.com', 'Website', 'Hà Nội', 'Khách mới, Quan tâm', 'Liên hệ buổi sáng'],
      ['Trần Thị B', '0987654321', 'b@example.com', 'Hotline', 'TP. Hồ Chí Minh', '', '']
    ]);
    worksheet['!cols'] = [
      { wch: 24 }, { wch: 18 }, { wch: 28 }, { wch: 18 }, { wch: 28 }, { wch: 28 }, { wch: 32 }
    ];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Khach hang');
    const output = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([output], { type: 'application/octet-stream' }), 'file-mau-import-khach-hang.xlsx');
  };

  const mappingRows = useMemo(() => headers.map((header, index) => ({
    key: index,
    index,
    header,
    samples: dataRows.slice(0, 3).map((row) => row[index]).filter((value) => String(value || '').trim())
  })), [headers, dataRows]);

  const handleMappingChange = (index, value) => {
    setMapping((previous) => previous.map((currentValue, currentIndex) => {
      if (currentIndex === index) return value;
      if (value && currentValue === value) return undefined;
      return currentValue;
    }));
  };

  const buildMappedCustomers = () => dataRows.map((row, rowIndex) => {
    const customer = { row_number: rowIndex + 2 };
    mapping.forEach((field, columnIndex) => {
      if (field) customer[field] = row[columnIndex];
    });
    return customer;
  });

  const loadPreview = async () => {
    const missingRequired = FIELD_OPTIONS.filter((field) => field.required && !mapping.includes(field.value));
    if (missingRequired.length) {
      message.error(`Vui lòng mapping: ${missingRequired.map((item) => item.label).join(', ')}`);
      return;
    }

    const customers = buildMappedCustomers();
    setMappedCustomers(customers);
    setPreviewLoading(true);
    try {
      const response = await fetchData({
        url: 'api/v1/customer/import/preview',
        method: 'POST',
        data: { customers }
      });
      setPreview(response);
      setActiveTab(response.summary.valid ? 'valid' : response.summary.duplicate ? 'duplicate' : 'error');
      setCurrent(2);
    } catch (error) {
      message.error(error.message || 'Không thể kiểm tra dữ liệu import');
    } finally {
      setPreviewLoading(false);
    }
  };

  const commitImport = async () => {
    if (!skipDuplicates) {
      message.warning('Vui lòng bật loại bỏ khách trùng để tiếp tục');
      return;
    }
    setImporting(true);
    try {
      const response = await fetchData({
        url: 'api/v1/customer/import/commit',
        method: 'POST',
        data: { customers: mappedCustomers, skip_duplicates: true }
      });
      setResult(response);
      setCurrent(3);
      onSuccess();
    } catch (error) {
      message.error(error.message || 'Import khách hàng thất bại');
    } finally {
      setImporting(false);
    }
  };

  const downloadErrorReport = () => {
    if (!preview) return;
    const reportRows = preview.rows.filter((row) => row.status !== 'valid').map((row) => ({
      'Dòng Excel': row.row_number,
      'Họ tên': row.full_name,
      'Số điện thoại': row.phone,
      'Email': row.email,
      'Trạng thái': row.status === 'duplicate' ? 'Khách trùng' : 'Dữ liệu lỗi',
      'Chi tiết': row.status === 'duplicate'
        ? `${row.duplicate_fields.join(', ')} - ${row.duplicate_sources.map((source) => source === 'system' ? 'Đã có trong hệ thống' : `Trùng dòng ${source.split(':')[1]}`).join(', ')}`
        : row.errors.join(', ')
    }));
    const worksheet = XLSX.utils.json_to_sheet(reportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Bao cao');
    const output = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([output], { type: 'application/octet-stream' }), 'bao-cao-import-khach-hang.xlsx');
  };

  const mappingColumns = [
    { title: 'Tên cột trong file', dataIndex: 'header', width: 220, render: (value) => <Text strong>{value}</Text> },
    {
      title: 'Dữ liệu mẫu', dataIndex: 'samples', render: (samples) => (
        <Space direction="vertical" size={2}>
          {samples.length ? samples.map((sample, index) => <Text type="secondary" key={index}>{String(sample)}</Text>) : <Text type="secondary">Không có dữ liệu</Text>}
        </Space>
      )
    },
    {
      title: 'Trường CRM tương ứng', dataIndex: 'index', width: 260, render: (index) => (
        <Select
          allowClear
          placeholder="Bỏ qua cột này"
          value={mapping[index]}
          onChange={(value) => handleMappingChange(index, value)}
          style={{ width: '100%' }}
        >
          {FIELD_OPTIONS.map((field) => (
            <Select.Option
              key={field.value}
              value={field.value}
              disabled={mapping.includes(field.value) && mapping[index] !== field.value}
            >
              {field.label}{field.required ? ' *' : ''}
            </Select.Option>
          ))}
        </Select>
      )
    }
  ];

  const previewRows = preview ? preview.rows.filter((row) => row.status === activeTab) : [];
  const previewColumns = [
    { title: 'Dòng', dataIndex: 'row_number', width: 70 },
    { title: 'Họ tên', dataIndex: 'full_name', ellipsis: true },
    { title: 'Số điện thoại', dataIndex: 'phone', width: 150 },
    { title: 'Email', dataIndex: 'email', ellipsis: true },
    {
      title: 'Kết quả', key: 'result', width: 260, render: (_, row) => {
        if (row.status === 'valid') return <Tag color="green">Hợp lệ</Tag>;
        if (row.status === 'error') return <Text type="danger">{row.errors.join(', ')}</Text>;
        return (
          <Text type="warning">
            {row.duplicate_fields.join(', ')} — {row.duplicate_sources.map((source) => source === 'system' ? 'đã có trong hệ thống' : `trùng dòng ${source.split(':')[1]}`).join(', ')}
          </Text>
        );
      }
    }
  ];

  const renderUpload = () => (
    <div>
      <Dragger
        accept=".xlsx,.xls"
        beforeUpload={handleFile}
        showUploadList={false}
        multiple={false}
        style={{ padding: '24px 0' }}
      >
        <p className="ant-upload-drag-icon"><InboxOutlined /></p>
        <p className="ant-upload-text">Kéo thả file Excel vào đây hoặc bấm để chọn file</p>
        <p className="ant-upload-hint">Hỗ trợ .xlsx, .xls — tối đa {MAX_ROWS.toLocaleString('vi-VN')} khách hàng, dung lượng 10MB</p>
      </Dragger>
      {file ? (
        <Alert
          style={{ marginTop: 16 }}
          type="success"
          showIcon
          message={file.name}
          description={`Đã đọc ${dataRows.length.toLocaleString('vi-VN')} dòng và ${headers.length} cột`}
        />
      ) : null}
      <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text type="secondary">File không bắt buộc đúng header mẫu — bạn sẽ được chọn mapping ở bước tiếp theo.</Text>
        <Button icon={<DownloadOutlined />} onClick={downloadTemplate}>Tải file Excel mẫu</Button>
      </div>
    </div>
  );

  const renderMapping = () => (
    <div>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Hệ thống đã tự gợi ý mapping theo tên cột"
        description="Bạn có thể đổi lại từng trường. Mỗi trường CRM chỉ được mapping với một cột; Họ tên và Số điện thoại là bắt buộc."
      />
      <Table
        size="small"
        columns={mappingColumns}
        dataSource={mappingRows}
        pagination={false}
        scroll={{ y: 360 }}
      />
    </div>
  );

  const renderPreview = () => preview && (
    <div>
      <Row gutter={12} style={{ marginBottom: 16 }}>
        <Col span={6}><div style={{ border: '1px solid #d9d9d9', padding: 12 }}><Statistic title="Tổng số dòng" value={preview.summary.total} /></div></Col>
        <Col span={6}><div style={{ border: '1px solid #b7eb8f', padding: 12 }}><Statistic title="Hợp lệ" value={preview.summary.valid} valueStyle={{ color: '#52c41a' }} /></div></Col>
        <Col span={6}><div style={{ border: '1px solid #ffe58f', padding: 12 }}><Statistic title="Khách trùng" value={preview.summary.duplicate} valueStyle={{ color: '#faad14' }} /></div></Col>
        <Col span={6}><div style={{ border: '1px solid #ffccc7', padding: 12 }}><Statistic title="Có lỗi" value={preview.summary.error} valueStyle={{ color: '#ff4d4f' }} /></div></Col>
      </Row>
      {preview.summary.duplicate ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={`Phát hiện ${preview.summary.duplicate} khách hàng bị trùng`}
          description="Hệ thống kiểm tra theo số điện thoại hoặc email, bao gồm dữ liệu đã có và các dòng lặp trong chính file."
        />
      ) : null}
      <div style={{ marginBottom: 12 }}>
        <Checkbox checked={skipDuplicates} onChange={(event) => setSkipDuplicates(event.target.checked)}>
          Loại bỏ toàn bộ khách trùng trước khi import
        </Checkbox>
      </div>
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab={<span><CheckCircleOutlined /> Hợp lệ ({preview.summary.valid})</span>} key="valid" />
        <TabPane tab={<span><ExclamationCircleOutlined /> Trùng ({preview.summary.duplicate})</span>} key="duplicate" />
        <TabPane tab={<span><CloseCircleOutlined /> Lỗi ({preview.summary.error})</span>} key="error" />
      </Tabs>
      <Table
        size="small"
        columns={previewColumns}
        dataSource={previewRows}
        rowKey="row_number"
        pagination={{ pageSize: 8, showSizeChanger: false }}
        scroll={{ y: 280 }}
      />
    </div>
  );

  const renderComplete = () => (
    <Result
      status="success"
      title={`Đã import ${result ? result.imported : 0} khách hàng`}
      subTitle={result ? `Đã loại ${result.duplicate} khách trùng và bỏ qua ${result.invalid} dòng lỗi.` : ''}
      extra={[
        preview && (preview.summary.duplicate || preview.summary.error) ? (
          <Button key="report" icon={<DownloadOutlined />} onClick={downloadErrorReport}>Tải báo cáo dòng bị loại</Button>
        ) : null
      ].filter(Boolean)}
    />
  );

  const footer = [];
  if (current > 0 && current < 3) footer.push(<Button key="back" disabled={previewLoading || importing} onClick={() => setCurrent(current - 1)}>Quay lại</Button>);
  if (current === 0) footer.push(<Button key="next" type="primary" disabled={!file} onClick={() => setCurrent(1)}>Tiếp tục</Button>);
  if (current === 1) footer.push(<Button key="preview" type="primary" loading={previewLoading} onClick={loadPreview}>Kiểm tra dữ liệu</Button>);
  if (current === 2) footer.push(
    <Button key="import" type="primary" loading={importing} disabled={!skipDuplicates || !preview || !preview.summary.valid} onClick={commitImport}>
      Import {preview ? preview.summary.valid : 0} khách hàng
    </Button>
  );
  if (current === 3) footer.push(<Button key="done" type="primary" onClick={onCancel}>Hoàn tất</Button>);

  return (
    <Modal
      title={<Space><FileExcelOutlined style={{ color: '#52c41a' }} /> Import khách hàng từ Excel</Space>}
      visible={visible}
      onCancel={() => !importing && onCancel()}
      footer={footer}
      width={1000}
      maskClosable={false}
      keyboard={!importing}
      destroyOnClose
      bodyStyle={{ maxHeight: '72vh', overflowY: 'auto' }}
    >
      <Steps current={current} size="small" style={{ marginBottom: 24 }}>
        <Step title="Tải file" />
        <Step title="Mapping cột" />
        <Step title="Kiểm tra dữ liệu" />
        <Step title="Hoàn tất" />
      </Steps>
      {current === 0 && renderUpload()}
      {current === 1 && renderMapping()}
      {current === 2 && renderPreview()}
      {current === 3 && renderComplete()}
      {current === 0 ? (
        <Paragraph type="secondary" style={{ marginTop: 16, marginBottom: 0 }}>
          Dữ liệu chỉ được gửi lên hệ thống sau khi bạn hoàn tất mapping và xác nhận danh sách hợp lệ.
        </Paragraph>
      ) : null}
    </Modal>
  );
};

export default CustomerImportModal;
